import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../../api/axiosClient";
import { formatMessageRate } from "../../../utils/currencyFormatters";

const SCOPED_PRICING_RULES_ENDPOINT = "/admin/billing/pricing-rules";
const ADMIN_PRICING_GRID_ENDPOINT = "/admin/billing/pricing-grid";
const ACTOR_PRICING_GRID_ENDPOINT = "/billing/pricing-grid";
const CATEGORY_OPTIONS = [
  { key: "marketing", label: "Marketing" },
  { key: "utility", label: "Utility" },
  { key: "authentication", label: "Authentication" },
  { key: "service", label: "Service" },
];
const COUNTRY_OPTIONS = [
  { value: "IN", label: "India" },
];
const MODE_OPTIONS = [
  { value: "Flat", label: "Flat Price" },
  { value: "MetaPlusFlat", label: "Meta + Flat Markup" },
  { value: "MetaPlusPercent", label: "Meta + Percent Markup" },
  { value: "Custom", label: "Custom Price" },
];

function extractEnvelopeData(response) {
  if (Array.isArray(response?.data)) return response.data;

  const payload = response?.data?.payload;
  if (Array.isArray(payload)) return payload;

  const data = response?.data?.data;
  if (Array.isArray(data)) return data;

  return [];
}

function getErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.errorMessage ||
    error?.message ||
    fallbackMessage
  );
}

function normalizeCategoryKey(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeCountryCode(value) {
  const normalized = String(value || "").trim().toUpperCase();
  return normalized || "";
}

function normalizeModeKey(value) {
  return String(value || "").trim().toLowerCase();
}

function isModeUsingFlatSellPrice(mode) {
  const normalized = normalizeModeKey(mode);
  return normalized === "flat" || normalized === "custom";
}

function makeEmptyDraft(countryCode = "") {
  return {
    pricingMode: "Flat",
    amount: "",
    minimumPrice: "",
    isActive: true,
    effectiveFromUtc: new Date().toISOString(),
    countryCode,
  };
}

function mapGridRowToDraft(row, fallbackCountryCode) {
  if (!row) return makeEmptyDraft(fallbackCountryCode);

  const normalizedMode = normalizeModeKey(row.pricingMode);
  const amount = isModeUsingFlatSellPrice(normalizedMode)
    ? row.flatSellPrice
    : normalizedMode === "metaplusflat"
      ? row.flatMarkup
      : row.percentMarkup;

  return {
    pricingMode: row.pricingMode || "Flat",
    amount:
      amount === null || amount === undefined || Number.isNaN(Number(amount))
        ? ""
        : String(amount),
    minimumPrice:
      row.minimumPrice === null ||
      row.minimumPrice === undefined ||
      Number.isNaN(Number(row.minimumPrice))
        ? ""
        : String(row.minimumPrice),
    isActive: row.isActive !== false,
    effectiveFromUtc: row.effectiveFromUtc || new Date().toISOString(),
    countryCode: normalizeCountryCode(row.countryCode) || fallbackCountryCode,
  };
}

function buildPayload(categoryKey, draft, selectedCountryCode) {
  const payload = {
    category: categoryKey,
    countryCode: normalizeCountryCode(selectedCountryCode) || null,
    pricingMode: draft.pricingMode,
    flatSellPrice: null,
    flatMarkup: null,
    percentMarkup: null,
    minimumPrice:
      draft.minimumPrice === "" ? null : Number(draft.minimumPrice),
    isActive: Boolean(draft.isActive),
    effectiveFromUtc: draft.effectiveFromUtc || new Date().toISOString(),
    effectiveToUtc: null,
  };

  const numericAmount = Number(draft.amount);
  if (isModeUsingFlatSellPrice(draft.pricingMode)) {
    payload.flatSellPrice = numericAmount;
  } else if (normalizeModeKey(draft.pricingMode) === "metaplusflat") {
    payload.flatMarkup = numericAmount;
  } else {
    payload.percentMarkup = numericAmount;
  }

  return payload;
}

function getAmountFieldLabel(mode) {
  if (isModeUsingFlatSellPrice(mode)) return "Sell Price";
  return normalizeModeKey(mode) === "metaplusflat"
    ? "Flat Markup"
    : "Percent Markup";
}

function getCountryLabel(countryCode) {
  const normalizedCountry = normalizeCountryCode(countryCode);
  return (
    COUNTRY_OPTIONS.find(option => option.value === normalizedCountry)?.label ||
    normalizedCountry ||
    "India"
  );
}

function parseOptionalNumber(value) {
  if (value === "" || value === null || value === undefined) return null;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function computeDraftPreviewPrice(row, draft, displayVariant) {
  const basePrice = displayVariant === "internal"
    ? parseOptionalNumber(row?.inheritedInputPrice)
    : parseOptionalNumber(row?.basePrice);
  const amount = parseOptionalNumber(draft.amount);
  const inheritedMinimumFloor = parseOptionalNumber(row?.inheritedMinimumFloor);
  const rowMinimum = parseOptionalNumber(draft.minimumPrice);

  let previewPrice = null;
  const normalizedMode = normalizeModeKey(draft.pricingMode);

  if (normalizedMode === "flat" || normalizedMode === "custom") {
    previewPrice = amount;
  } else if (normalizedMode === "metaplusflat" && basePrice !== null && amount !== null) {
    previewPrice = basePrice + amount;
  } else if (normalizedMode === "metapluspercent" && basePrice !== null && amount !== null) {
    previewPrice = basePrice + (basePrice * amount / 100);
  }

  const minimumFloor = [inheritedMinimumFloor, rowMinimum]
    .filter(value => value !== null)
    .reduce((highest, value) => Math.max(highest, value), Number.NEGATIVE_INFINITY);

  if (previewPrice !== null && minimumFloor !== Number.NEGATIVE_INFINITY) {
    previewPrice = Math.max(previewPrice, minimumFloor);
  }

  return previewPrice;
}

function formatCompactRate(value, currency) {
  return formatMessageRate(value, currency, { compact: true });
}

function formatSummaryRate(value, currency) {
  return formatCompactRate(value, currency).replace(" / msg", "/msg");
}

function RateDisplay({ value, currency }) {
  return (
    <div className="w-full min-w-[128px] whitespace-nowrap rounded-[10px] border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs font-semibold tabular-nums text-slate-700 sm:text-[13px]">
      {formatCompactRate(value, currency)}
    </div>
  );
}

function formatSummaryLine(row, displayVariant, previewPrice) {
  if (!row) return "No rule configured for this filter.";

  const currency = row.currency || "INR";
  if (displayVariant === "internal") {
    return [
      `Meta ${formatSummaryRate(row.metaCost, currency)}`,
      `Inherited ${formatSummaryRate(row.inheritedInputPrice, currency)}`,
      `Final ${formatSummaryRate(previewPrice ?? row.previewSellPrice, currency)}`,
    ].join(" · ");
  }

  return [
    `Base ${formatSummaryRate(row.basePrice, currency)}`,
    `Final ${formatSummaryRate(previewPrice ?? row.previewSellPrice, currency)}`,
  ].join(" · ");
}

function getPlatformSourceBadge(row) {
  if (!row) {
    return {
      label: "Meta fallback",
      className: "bg-sky-50 text-sky-700",
    };
  }

  return row.ruleId
    ? {
        label: "Platform rule",
        className: "bg-indigo-50 text-indigo-700",
      }
    : {
        label: "Meta fallback",
        className: "bg-sky-50 text-sky-700",
      };
}

export default function ScopedPricingEditor({
  scopeLevel,
  scopeEntityId,
  scopeLabel,
  description,
  emptyStateTitle = "Select a target to configure pricing.",
  emptyStateBody = "",
  readMode = "admin",
  displayVariant = "internal",
  readEndpoint,
}) {
  const [selectedCountryCode, setSelectedCountryCode] = useState("IN");
  const [gridRows, setGridRows] = useState([]);
  const [drafts, setDrafts] = useState(() =>
    Object.fromEntries(CATEGORY_OPTIONS.map(item => [item.key, makeEmptyDraft()]))
  );
  const [rowErrors, setRowErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [savingCategory, setSavingCategory] = useState("");
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const resolvedReadEndpoint = readEndpoint ||
    (readMode === "actor" ? ACTOR_PRICING_GRID_ENDPOINT : ADMIN_PRICING_GRID_ENDPOINT);

  const hasScope =
    scopeLevel === "Platform" || Boolean(scopeEntityId);
  const displayCountryCode = normalizeCountryCode(selectedCountryCode);

  const existingRowMap = useMemo(() => {
    const map = new Map();
    CATEGORY_OPTIONS.forEach(item => {
      const row = (Array.isArray(gridRows) ? gridRows : []).find(
        candidate => normalizeCategoryKey(candidate?.category) === item.key
      ) || null;
      map.set(item.key, row);
    });
    return map;
  }, [gridRows]);

  const baselineDrafts = useMemo(() => {
    const nextDrafts = {};
    CATEGORY_OPTIONS.forEach(item => {
      nextDrafts[item.key] = mapGridRowToDraft(
        existingRowMap.get(item.key),
        displayCountryCode
      );
    });
    return nextDrafts;
  }, [displayCountryCode, existingRowMap]);

  useEffect(() => {
    setDrafts(baselineDrafts);
    setRowErrors({});
  }, [baselineDrafts]);

  const loadGrid = useCallback(async () => {
    if (!hasScope) {
      setGridRows([]);
      setLoadError("");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setLoadError("");

      const params = readMode === "actor"
        ? {
            ...(displayCountryCode ? { countryCode: displayCountryCode } : {}),
          }
        : {
            scopeLevel,
            ...(scopeLevel === "Platform" ? {} : { scopeEntityId }),
            ...(displayCountryCode ? { countryCode: displayCountryCode } : {}),
          };

      const response = await axiosClient.get(resolvedReadEndpoint, {
        params,
        __skipBizHeader: true,
        __silent401: true,
        __silent403: true,
      });

      setGridRows(extractEnvelopeData(response));
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Failed to load pricing grid."
      );
      setGridRows([]);
      setLoadError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [
    displayCountryCode,
    hasScope,
    readMode,
    resolvedReadEndpoint,
    scopeEntityId,
    scopeLevel,
  ]);

  useEffect(() => {
    loadGrid();
  }, [loadGrid]);

  const updateDraft = (categoryKey, patch) => {
    setDrafts(current => ({
      ...current,
      [categoryKey]: { ...current[categoryKey], ...patch },
    }));
    setRowErrors(current => ({ ...current, [categoryKey]: "" }));
  };

  const validateDraft = draft => {
    const amount = Number(draft.amount);
    const minimumPrice =
      draft.minimumPrice === "" ? null : Number(draft.minimumPrice);

    if (!draft.amount.trim() || !Number.isFinite(amount) || amount < 0) {
      return "Enter a valid non-negative amount.";
    }

    if (
      minimumPrice !== null &&
      (!Number.isFinite(minimumPrice) || minimumPrice < 0)
    ) {
      return "Minimum price must be empty or a non-negative number.";
    }

    return "";
  };

  const saveCategory = async (categoryKey, { silentSuccess = false } = {}) => {
    const draft = drafts[categoryKey];
    const validationError = validateDraft(draft);
    if (validationError) {
      setRowErrors(current => ({
        ...current,
        [categoryKey]: validationError,
      }));
      return { success: false, errorMessage: validationError };
    }

    const existingRow = existingRowMap.get(categoryKey);
    const payload = buildPayload(categoryKey, draft, selectedCountryCode);

    try {
      setSavingCategory(categoryKey);
      setRowErrors(current => ({ ...current, [categoryKey]: "" }));

      if (existingRow?.ruleId) {
        await axiosClient.put(
          `${SCOPED_PRICING_RULES_ENDPOINT}/${existingRow.ruleId}`,
          payload,
          {
            __skipBizHeader: true,
          }
        );
      } else {
        await axiosClient.post(
          SCOPED_PRICING_RULES_ENDPOINT,
          {
            scopeLevel,
            scopeEntityId: scopeLevel === "Platform" ? null : scopeEntityId,
            ...payload,
          },
          {
            __skipBizHeader: true,
          }
        );
      }

      if (!silentSuccess) {
        toast.success("Pricing rule saved.");
      }
      await loadGrid();
      return { success: true };
    } catch (error) {
      const message = getErrorMessage(error, "Failed to save pricing rule.");
      setRowErrors(current => ({ ...current, [categoryKey]: message }));
      if (!silentSuccess) {
        toast.error(message);
      }
      return { success: false, errorMessage: message };
    } finally {
      setSavingCategory("");
    }
  };

  const deactivateCategory = async categoryKey => {
    const existingRow = existingRowMap.get(categoryKey);
    if (!existingRow?.ruleId) return;

    try {
      setSavingCategory(categoryKey);
      setRowErrors(current => ({ ...current, [categoryKey]: "" }));
      await axiosClient.post(
        `${SCOPED_PRICING_RULES_ENDPOINT}/${existingRow.ruleId}/deactivate`,
        {},
        { __skipBizHeader: true }
      );
      toast.success("Pricing rule deactivated.");
      await loadGrid();
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Failed to deactivate pricing rule."
      );
      setRowErrors(current => ({ ...current, [categoryKey]: message }));
      toast.error(message);
    } finally {
      setSavingCategory("");
    }
  };

  const activateCategory = async categoryKey => {
    const existingRow = existingRowMap.get(categoryKey);
    if (!existingRow?.ruleId) return;

    const draft = drafts[categoryKey] || makeEmptyDraft(displayCountryCode);
    const activeDraft = { ...draft, isActive: true };
    const validationError = validateDraft(activeDraft);
    if (validationError) {
      setRowErrors(current => ({
        ...current,
        [categoryKey]: validationError,
      }));
      return;
    }

    try {
      setSavingCategory(categoryKey);
      setRowErrors(current => ({ ...current, [categoryKey]: "" }));
      await axiosClient.put(
        `${SCOPED_PRICING_RULES_ENDPOINT}/${existingRow.ruleId}`,
        buildPayload(categoryKey, activeDraft, selectedCountryCode),
        {
          __skipBizHeader: true,
        }
      );
      toast.success("Pricing rule activated.");
      await loadGrid();
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Failed to activate pricing rule."
      );
      setRowErrors(current => ({ ...current, [categoryKey]: message }));
      toast.error(message);
    } finally {
      setSavingCategory("");
    }
  };

  const dirtyCategoryKeys = useMemo(
    () =>
      CATEGORY_OPTIONS.filter(item => {
        const currentDraft = drafts[item.key] || makeEmptyDraft(displayCountryCode);
        const baselineDraft = baselineDrafts[item.key] || makeEmptyDraft(displayCountryCode);
        return JSON.stringify(currentDraft) !== JSON.stringify(baselineDraft);
      }).map(item => item.key),
    [baselineDrafts, displayCountryCode, drafts]
  );

  const hasDirtyChanges = dirtyCategoryKeys.length > 0;

  const resetAllDrafts = () => {
    setDrafts(baselineDrafts);
    setRowErrors({});
  };

  const saveAllChanges = async () => {
    if (!hasDirtyChanges) return;

    const nextErrors = {};
    dirtyCategoryKeys.forEach(categoryKey => {
      const validationError = validateDraft(drafts[categoryKey]);
      if (validationError) {
        nextErrors[categoryKey] = validationError;
      }
    });

    if (Object.keys(nextErrors).length > 0) {
      setRowErrors(current => ({ ...current, ...nextErrors }));
      toast.error("Fix validation errors before saving.");
      return;
    }

    setIsBulkSaving(true);

    let savedCount = 0;
    let firstFailureMessage = "";

    for (const categoryKey of dirtyCategoryKeys) {
      const result = await saveCategory(categoryKey, { silentSuccess: true });
      if (result.success) {
        savedCount += 1;
      } else if (!firstFailureMessage) {
        firstFailureMessage =
          result.errorMessage || "Some pricing rules could not be saved.";
      }
    }

    setIsBulkSaving(false);

    if (savedCount > 0) {
      toast.success(
        savedCount === 1
          ? "1 pricing rule saved."
          : `${savedCount} pricing rules saved.`
      );
    }

    if (savedCount !== dirtyCategoryKeys.length) {
      toast.error(firstFailureMessage || "Some pricing rules could not be saved.");
    }
  };

  if (!hasScope) {
    return (
      <div className="rounded-[10px] border border-slate-200 bg-white px-5 py-12 text-center shadow-sm">
        <div className="text-base font-semibold text-slate-900">
          {emptyStateTitle}
        </div>
        {emptyStateBody ? (
          <div className="mt-2 text-sm text-slate-500">{emptyStateBody}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-[10px] border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-1">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              Scope
            </div>
            <div className="text-base font-semibold text-slate-900">
              {scopeLabel}
            </div>
            {description ? (
              <div className="text-sm text-slate-500">{description}</div>
            ) : null}
          </div>

          <label className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              Country Filter
            </span>
            <select
              value={selectedCountryCode}
              onChange={event => setSelectedCountryCode(event.target.value)}
              className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 lg:w-[180px]"
            >
              {COUNTRY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {loadError ? (
          <div className="mt-4 rounded-[10px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {loadError}
          </div>
        ) : null}

        <div className="mt-4 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-slate-500">
            {hasDirtyChanges
              ? `${dirtyCategoryKeys.length} categor${dirtyCategoryKeys.length === 1 ? "y has" : "ies have"} unsaved changes.`
              : "No unsaved changes."}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={resetAllDrafts}
              disabled={!hasDirtyChanges || isBulkSaving || Boolean(savingCategory)}
              className="inline-flex items-center rounded-[10px] border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
            >
              Discard Changes
            </button>
            <button
              type="button"
              onClick={saveAllChanges}
              disabled={!hasDirtyChanges || isBulkSaving || Boolean(savingCategory)}
              className="inline-flex items-center rounded-[10px] bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isBulkSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="px-5 py-10">
          <div className="flex items-center justify-center gap-3 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
            <span>Loading pricing grid...</span>
          </div>
        </div>
      ) : (
        <div className="px-5 py-5">
          <div className="space-y-4">
            {CATEGORY_OPTIONS.map(item => {
              const draft = drafts[item.key] || makeEmptyDraft(displayCountryCode);
              const existingRow = existingRowMap.get(item.key);
              const isSaving = savingCategory === item.key;
              const amountLabel = getAmountFieldLabel(draft.pricingMode);
              const currency = existingRow?.currency || "INR";
              const draftPreviewPrice = computeDraftPreviewPrice(
                existingRow,
                draft,
                displayVariant
              );
              const platformSourceBadge = scopeLevel === "Platform"
                ? getPlatformSourceBadge(existingRow)
                : null;

              return (
                <div
                  key={item.key}
                  className="rounded-[12px] border border-slate-200 bg-white p-4 shadow-sm transition hover:bg-slate-50/50"
                >
                  <div className="space-y-4">
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-base font-semibold text-slate-900">
                          {item.label}
                        </span>
                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          {getCountryLabel(displayCountryCode)}
                        </span>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                            existingRow?.isActive === false
                              ? "bg-amber-50 text-amber-700"
                              : "bg-emerald-50 text-emerald-700"
                          }`}
                        >
                          {existingRow?.statusLabel || "Editable"}
                        </span>
                        {platformSourceBadge ? (
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${platformSourceBadge.className}`}
                          >
                            {platformSourceBadge.label}
                          </span>
                        ) : null}
                      </div>
                      <div className="text-xs leading-5 text-slate-500">
                        {formatSummaryLine(existingRow, displayVariant, draftPreviewPrice)}
                      </div>
                      <div className="text-xs leading-5 text-slate-400">
                        {existingRow?.previewDescription || "No scoped rule configured."}
                      </div>
                      {rowErrors[item.key] ? (
                        <div className="text-sm font-medium text-rose-600">
                          {rowErrors[item.key]}
                        </div>
                      ) : null}
                    </div>

                    <div
                      className={`grid gap-3 ${
                        displayVariant === "internal" ? "md:grid-cols-3" : "md:grid-cols-2"
                      }`}
                    >
                      {displayVariant === "internal" ? (
                        <>
                          <div className="min-w-0 space-y-1.5">
                            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                            Meta Price
                            </span>
                            <RateDisplay value={existingRow?.metaCost} currency={currency} />
                          </div>
                          <div className="min-w-0 space-y-1.5">
                            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                            Inherited Price
                            </span>
                            <RateDisplay value={existingRow?.inheritedInputPrice} currency={currency} />
                          </div>
                        </>
                      ) : (
                        <div className="min-w-0 space-y-1.5">
                          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Base Price
                          </span>
                          <RateDisplay value={existingRow?.basePrice} currency={currency} />
                        </div>
                      )}

                      <div className="min-w-0 space-y-1.5">
                        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Final Price
                        </span>
                        <RateDisplay
                          value={draftPreviewPrice ?? existingRow?.previewSellPrice}
                          currency={currency}
                        />
                      </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                      <label className="min-w-0 space-y-1.5">
                        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Pricing Mode
                        </span>
                        <select
                          value={draft.pricingMode}
                          onChange={event =>
                            updateDraft(item.key, { pricingMode: event.target.value })
                          }
                          className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        >
                          {MODE_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="min-w-0 space-y-1.5">
                        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          {amountLabel}
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={draft.amount}
                          onChange={event =>
                            updateDraft(item.key, { amount: event.target.value })
                          }
                          placeholder={amountLabel}
                          className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        />
                      </label>

                      <div className="min-w-0 space-y-1.5">
                        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Final Price
                        </span>
                        <RateDisplay
                          value={draftPreviewPrice ?? existingRow?.previewSellPrice}
                          currency={currency}
                        />
                      </div>

                      <label className="min-w-0 space-y-1.5">
                        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Minimum Price
                        </span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={draft.minimumPrice}
                          onChange={event =>
                            updateDraft(item.key, { minimumPrice: event.target.value })
                          }
                          placeholder="Optional"
                          className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        />
                      </label>

                      <label className="min-w-0 space-y-1.5">
                        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Effective From
                        </span>
                        <input
                          type="datetime-local"
                          value={String(draft.effectiveFromUtc || "")
                            .replace("Z", "")
                            .slice(0, 16)}
                          onChange={event =>
                            updateDraft(item.key, {
                              effectiveFromUtc: event.target.value
                                ? new Date(event.target.value).toISOString()
                                : new Date().toISOString(),
                            })
                          }
                          className="w-full rounded-[10px] border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        />
                      </label>
                    </div>

                    <div className="flex flex-wrap justify-end gap-2">
                      {existingRow?.ruleId ? (
                        <button
                          type="button"
                          onClick={() =>
                            existingRow?.isActive === false
                              ? activateCategory(item.key)
                              : deactivateCategory(item.key)
                          }
                          disabled={isSaving || isBulkSaving}
                          className={`inline-flex min-w-[112px] items-center justify-center rounded-[10px] border px-3 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-70 ${
                            existingRow?.isActive === false
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                              : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                          }`}
                        >
                          {existingRow?.isActive === false ? "Activate" : "Deactivate"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
